import axios from 'axios';
import { getCookie } from '../../utils/cookieUtils';

// Base URLs for different services
const API_ENDPOINTS = {
  GET_ALL_SCREENERS: 'https://d6ewrw79kg.execute-api.ap-south-1.amazonaws.com/default/getAllScreeners',
  VIEW_SCREENER_DETAILS: 'https://cmtdqn92t6.execute-api.ap-south-1.amazonaws.com/default/viewScreenerDetails',
  GET_STOCK_SPECIFIC: 'https://42l9irgw2e.execute-api.ap-south-1.amazonaws.com/default/getStockSpecific',
  GET_STOCK_TECHNICALS: 'https://c4cparyxx3.execute-api.ap-south-1.amazonaws.com/default/getStockTechnicals',
  GET_TREND_FROM_SYMBOL: 'https://9j943kf49c.execute-api.ap-south-1.amazonaws.com/default/getTrendFromSymbol',
  GET_UPDATE_WATCHLIST: 'https://lhijbdwvj4.execute-api.ap-south-1.amazonaws.com/default/getUpdatePositionWatchlist',
};

/**
 * Get all screeners for a specific category
 * @param {string} path - Category path: 'candlesticks', 'scanners', or 'technicals'
 * @returns {Promise} Screener data
 */
export const getAllScreeners = async (path) => {
  try {
    const response = await axios.post(API_ENDPOINTS.GET_ALL_SCREENERS, {
      path: path.toLowerCase(),
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching screeners for ${path}:`, error);
    throw error;
  }
};

/**
 * Get detailed screener data with filters
 * @param {Object} params - Filter parameters
 * @param {string} params.name - Screener name
 * @param {string} params.path - Category path
 * @param {string} params.selected_group - Stock group (e.g., 'NIFTY 50')
 * @param {string} params.selected_timeframe - Timeframe (e.g., '1day')
 * @returns {Promise} Detailed screener data with stocks
 */
export const viewScreenerDetails = async ({ name, path, selected_group, selected_timeframe }) => {
  try {
    if (!name) {
      throw new Error('Screener name is required');
    }

    const payload = {
      name: typeof name === 'string' ? name.trim() : name,
      path: typeof path === 'string' ? path.toLowerCase() : path,
      selected_group,
      selected_timeframe,
    };

    console.log('Sending request to viewScreenerDetails:', payload);

    const response = await axios.post(API_ENDPOINTS.VIEW_SCREENER_DETAILS, payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching screener details:', error);
    throw error;
  }
};

/**
 * Get stock-specific data including candlesticks, pivot levels, etc.
 * @param {string} stock - Stock symbol
 * @param {string} type - Type: 'Intraday' or 'Delivery'
 * @returns {Promise} Stock-specific data
 */
export const getStockSpecific = async (stock, type = 'Intraday') => {
  try {
    const response = await axios.post(API_ENDPOINTS.GET_STOCK_SPECIFIC, {
      stock,
      type,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock specific data:', error);
    throw error;
  }
};

/**
 * Get technical indicators for a stock
 * @param {string} stockName - Stock symbol
 * @param {string} selected_timeframe - Timeframe (e.g., '1day')
 * @returns {Promise} Technical indicators data
 */
export const getStockTechnicals = async (stockName, selected_timeframe) => {
  try {
    const response = await axios.post(API_ENDPOINTS.GET_STOCK_TECHNICALS, {
      stockName,
      selected_timeframe,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock technicals:', error);
    throw error;
  }
};

/**
 * Get trend analysis for a stock symbol
 * @param {string} symbol - Stock symbol
 * @returns {Promise} Trend data
 */
export const getTrendFromSymbol = async (symbol) => {
  try {
    const response = await axios.post(API_ENDPOINTS.GET_TREND_FROM_SYMBOL, {
      symbol,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching trend data:', error);
    throw error;
  }
};

/**
 * Get all watchlists for the user
 * @returns {Promise} Array of watchlists
 */
export const getAllWatchlists = async () => {
  try {
    const userId = getCookie('client_id');
    const accessToken = getCookie('access_token');

    const response = await axios.post(API_ENDPOINTS.GET_UPDATE_WATCHLIST, {
      user_id: userId,
      operation: 'get',
      access_token: accessToken,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching watchlists:', error);
    throw error;
  }
};

/**
 * Create a new watchlist
 * @param {string} listName - Name of the watchlist
 * @returns {Promise} Created watchlist data
 */
export const createWatchlist = async (listName) => {
  try {
    const userId = getCookie('client_id');
    const accessToken = getCookie('access_token');

    const response = await axios.post(API_ENDPOINTS.GET_UPDATE_WATCHLIST, {
      user_id: userId,
      operation: 'create',
      list_name: listName,
      page: '',
      access_token: accessToken,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating watchlist:', error);
    throw error;
  }
};

/**
 * Delete a watchlist
 * @param {string} watchlistId - ID of the watchlist to delete
 * @returns {Promise} Deletion response
 */
export const deleteWatchlist = async (watchlistId) => {
  try {
    const userId = getCookie('client_id');
    const accessToken = getCookie('access_token');

    const response = await axios.post(API_ENDPOINTS.GET_UPDATE_WATCHLIST, {
      user_id: userId,
      operation: 'delete',
      page: watchlistId,
      access_token: accessToken,
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting watchlist:', error);
    throw error;
  }
};

/**
 * Add multiple symbols to a watchlist
 * @param {string} watchlistId - ID of the watchlist
 * @param {Array} instruments - Array of instrument objects
 * @returns {Promise} Addition response
 */
export const addMultipleSymbolsToWatchlist = async (watchlistId, instruments) => {
  try {
    const userId = getCookie('client_id');
    const accessToken = getCookie('access_token');

    // Map instruments to the required format
    const formattedInstruments = instruments.map((instrument) => ({
      trading_symbol: instrument.tradingSymbol || instrument.stock,
      exchange: instrument.exchange,
      code: instrument.code,
      formatted_ins_name: instrument.tradingSymbol || instrument.stock,
      name: instrument.companyName || instrument.stock,
      lot_size: '1',
      segment_type: mapExchangeToSegment(instrument.exchange),
    }));

    const response = await axios.post(API_ENDPOINTS.GET_UPDATE_WATCHLIST, {
      user_id: userId,
      operation: 'add_multiple',
      page: watchlistId,
      instruments: formattedInstruments,
      access_token: accessToken,
    });
    return response.data;
  } catch (error) {
    console.error('Error adding symbols to watchlist:', error);
    throw error;
  }
};

/**
 * Map exchange to segment type
 * @param {string} exchange - Exchange code
 * @returns {string} Segment type
 */
const mapExchangeToSegment = (exchange) => {
  const mapping = {
    NSE: 'nse_cm',
    NFO: 'nse_fo',
    BSE: 'bse_cm',
    MCX: 'mcx_fo',
  };
  return mapping[exchange?.toUpperCase()] || 'nse_cm';
};

/**
 * Export table data to CSV
 * @param {Array} data - Table data to export
 * @param {string} filename - Name of the CSV file
 */
export const exportToCSV = (data, filename = 'scanner_data.csv') => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from the first object
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Handle values that might contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
